import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { placeAPI, locationAPI, subLocationAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin, Loader2, Image, Youtube, ExternalLink, Compass, Instagram, X, ChevronDown, ChevronUp, HelpCircle, Bus, Train, Car, Ticket, Sun, AlertTriangle, ListChecks } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUpload from "@/components/ImageUpload";

const PLACE_CATEGORIES = ["Restaurants", "Temples", "Beaches", "Waterfalls", "Forts", "Parks"];
const BEST_SEASONS = ["All Year", "October - February", "November - January", "March - May", "June - September", "Post-Monsoon (Oct-Dec)", "Winter (Nov-Feb)", "Summer (Mar-May)"];

const categoryIcons = {
  Restaurants: "🍽️",
  Temples: "🛕",
  Beaches: "🏖️",
  Waterfalls: "💦",
  Forts: "🏰",
  Parks: "🌳",
};

const AdminPlaces = () => {
  const { token } = useAuth();
  const [places, setPlaces] = useState([]);
  const [locations, setLocations] = useState([]);
  const [subLocations, setSubLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlace, setEditingPlace] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterLocationId, setFilterLocationId] = useState("");
  const [activeTab, setActiveTab] = useState("basic");

  const emptyForm = {
    name: "",
    slug: "",
    description: "",
    category: "",
    address: "",
    map_link: "",
    image: "",
    images: [],
    youtube_urls: [],
    instagram_reel_urls: [],
    location_id: "",
    sub_location_id: "",
    nearest_bus_stand: "",
    nearest_railway_station: "",
    distance_from_mumbai: "",
    distance_from_pune: "",
    entry_fee: "",
    things_to_do: [],
    things_to_avoid: [],
    best_season: "",
    faqs: [],
    meta_title: "",
    meta_description: "",
    canonical_url: "",
  };

  const [form, setForm] = useState(emptyForm);
  const [newYoutubeUrl, setNewYoutubeUrl] = useState("");
  const [newInstagramUrl, setNewInstagramUrl] = useState("");
  const [newTodo, setNewTodo] = useState("");
  const [newAvoid, setNewAvoid] = useState("");

  useEffect(() => { fetchPlaces(); fetchLocations(); }, []);
  useEffect(() => {
    if (form.location_id) fetchSubLocations(form.location_id);
    else setSubLocations([]);
  }, [form.location_id]);
  useEffect(() => { fetchPlaces(); }, [filterCategory, filterLocationId]);

  const fetchPlaces = async () => {
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      if (filterLocationId) params.location_id = filterLocationId;
      const response = await placeAPI.getAll(params);
      setPlaces(response.data);
    } catch { toast.error("Failed to fetch places"); }
    finally { setLoading(false); }
  };

  const fetchLocations = async () => {
    try { const r = await locationAPI.getAll(); setLocations(r.data); } catch {}
  };

  const fetchSubLocations = async (locationId) => {
    try { const r = await subLocationAPI.getAll({ location_id: locationId }); setSubLocations(r.data); } catch {}
  };

  const resetForm = () => {
    setForm({ ...emptyForm, location_id: filterLocationId || "" });
    setEditingPlace(null);
    setNewYoutubeUrl(""); setNewInstagramUrl(""); setNewTodo(""); setNewAvoid("");
    setActiveTab("basic");
  };

  const handleEdit = (place) => {
    setEditingPlace(place);
    const youtubeUrls = place.youtube_urls?.length > 0 ? place.youtube_urls : (place.youtube_url ? [place.youtube_url] : []);
    const instagramUrls = place.instagram_reel_urls?.length > 0 ? place.instagram_reel_urls : (place.instagram_reel_url ? [place.instagram_reel_url] : []);
    setForm({
      name: place.name || "",
      slug: place.slug || "",
      description: place.description || "",
      category: place.category || "",
      address: place.address || "",
      map_link: place.map_link || "",
      image: place.image || "",
      images: place.images || [],
      youtube_urls: youtubeUrls,
      instagram_reel_urls: instagramUrls,
      location_id: place.location_id || "",
      sub_location_id: place.sub_location_id || "",
      nearest_bus_stand: place.nearest_bus_stand || "",
      nearest_railway_station: place.nearest_railway_station || "",
      distance_from_mumbai: place.distance_from_mumbai || "",
      distance_from_pune: place.distance_from_pune || "",
      entry_fee: place.entry_fee || "",
      things_to_do: place.things_to_do || [],
      things_to_avoid: place.things_to_avoid || [],
      best_season: place.best_season || "",
      faqs: place.faqs || [],
      meta_title: place.meta_title || "",
      meta_description: place.meta_description || "",
      canonical_url: place.canonical_url || "",
    });
    if (place.location_id) fetchSubLocations(place.location_id);
    setNewYoutubeUrl(""); setNewInstagramUrl(""); setNewTodo(""); setNewAvoid("");
    setActiveTab("basic");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category) { toast.error("Please fill in name and category"); return; }
    setSaving(true);
    try {
      if (editingPlace) {
        await placeAPI.update(editingPlace.id, form, token);
        toast.success("Place updated");
      } else {
        await placeAPI.create(form, token);
        toast.success("Place created");
      }
      setShowModal(false);
      resetForm();
      fetchPlaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save place");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this place?")) return;
    try { await placeAPI.delete(id, token); toast.success("Place deleted"); fetchPlaces(); }
    catch (error) { toast.error(error.response?.data?.detail || "Failed to delete place"); }
  };

  const addFaq = () => setForm({ ...form, faqs: [...form.faqs, { question: "", answer: "" }] });
  const updateFaq = (idx, field, val) => {
    const updated = [...form.faqs];
    updated[idx] = { ...updated[idx], [field]: val };
    setForm({ ...form, faqs: updated });
  };
  const removeFaq = (idx) => setForm({ ...form, faqs: form.faqs.filter((_, i) => i !== idx) });

  const removeImage = (idx) => setForm({ ...form, images: form.images.filter((_, i) => i !== idx) });

  const groupedPlaces = places.reduce((acc, place) => {
    const cat = place.category || "Other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(place);
    return acc;
  }, {});

  const tabs = [
    { id: "basic", label: "Basic Info" },
    { id: "media", label: "Media" },
    { id: "travel", label: "Travel Info" },
    { id: "details", label: "Details" },
    { id: "faqs", label: "FAQs" },
    { id: "seo", label: "SEO" },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-places">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-foreground mb-2">Places of Interest</h1>
            <p className="text-muted-foreground">Manage nearby attractions and points of interest</p>
          </div>
          <Button onClick={() => { resetForm(); setShowModal(true); }} className="rounded-full" data-testid="add-place-btn">
            <Plus className="w-4 h-4 mr-2" />Add Place
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-soft flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground whitespace-nowrap">Category:</label>
            <Select value={filterCategory || "all"} onValueChange={(val) => setFilterCategory(val === "all" ? "" : val)}>
              <SelectTrigger className="w-48" data-testid="filter-category"><SelectValue placeholder="All Categories" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {PLACE_CATEGORIES.map((cat) => (<SelectItem key={cat} value={cat}>{categoryIcons[cat]} {cat}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground whitespace-nowrap">Location:</label>
            <Select value={filterLocationId || "all"} onValueChange={(val) => setFilterLocationId(val === "all" ? "" : val)}>
              <SelectTrigger className="w-48" data-testid="filter-location"><SelectValue placeholder="All Locations" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map((loc) => (<SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <span className="text-sm text-muted-foreground ml-auto">{places.length} place{places.length !== 1 ? "s" : ""} found</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : places.length > 0 ? (
          <div className="space-y-8">
            {Object.entries(groupedPlaces).map(([category, categoryPlaces]) => (
              <div key={category} className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <span className="text-2xl">{categoryIcons[category] || "📍"}</span>{category}
                  <span className="text-sm font-normal text-muted-foreground">({categoryPlaces.length})</span>
                </h3>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {categoryPlaces.map((place) => (
                    <div key={place.id} className="bg-white rounded-xl shadow-soft overflow-hidden group" data-testid={`place-item-${place.id}`}>
                      <div className="aspect-video bg-muted relative overflow-hidden">
                        {place.image ? (
                          <img src={place.image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${place.image}` : place.image} alt={place.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Compass className="w-12 h-12 text-muted-foreground/50" /></div>
                        )}
                        <div className="absolute top-2 left-2">
                          <span className="px-2 py-1 bg-white/90 text-xs font-medium rounded-full">{categoryIcons[place.category]} {place.category}</span>
                        </div>
                        {(place.images?.length > 0) && (
                          <span className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded-full">{place.images.length + 1} imgs</span>
                        )}
                      </div>
                      <div className="p-4">
                        <h4 className="font-semibold text-foreground mb-1">{place.name}</h4>
                        {place.location_name && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                            <MapPin className="w-3 h-3" />{place.sub_location_name ? `${place.sub_location_name}, ` : ""}{place.location_name}
                          </p>
                        )}
                        {place.best_season && <p className="text-xs text-primary mb-1"><Sun className="w-3 h-3 inline mr-1" />{place.best_season}</p>}
                        {place.entry_fee && <p className="text-xs text-muted-foreground mb-1"><Ticket className="w-3 h-3 inline mr-1" />{place.entry_fee}</p>}
                        <div className="flex items-center gap-2 mt-2">
                          {place.map_link && <a href={place.map_link} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Map</a>}
                          {(place.youtube_urls?.length > 0 || place.youtube_url) && <span className="text-xs text-red-500 flex items-center gap-1"><Youtube className="w-3 h-3" /> {place.youtube_urls?.length || 1}</span>}
                          {(place.faqs?.length > 0) && <span className="text-xs text-blue-500 flex items-center gap-1"><HelpCircle className="w-3 h-3" /> {place.faqs.length} FAQ</span>}
                        </div>
                        <div className="flex gap-2 mt-3 pt-3 border-t">
                          <a href={`/place/${place.slug || place.id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-coconut-green hover:underline" data-testid={`view-place-${place.id}`}><ExternalLink className="w-3 h-3" /> View Live</a>
                          <div className="flex gap-2 ml-auto">
                            <Button variant="outline" size="sm" onClick={() => handleEdit(place)} data-testid={`edit-place-${place.id}`}><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
                            <Button variant="outline" size="sm" onClick={() => handleDelete(place.id)} className="text-red-500 hover:bg-red-50" data-testid={`delete-place-${place.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 shadow-soft text-center">
            <Compass className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Places Yet</h3>
            <p className="text-muted-foreground mb-6">Add nearby attractions, restaurants, temples, and more.</p>
            <Button onClick={() => { resetForm(); setShowModal(true); }}><Plus className="w-4 h-4 mr-2" />Add First Place</Button>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPlace ? "Edit Place" : "Add New Place"}</DialogTitle>
            </DialogHeader>

            {/* Tabs */}
            <div className="flex gap-1 border-b mb-4 overflow-x-auto">
              {tabs.map((t) => (
                <button key={t.id} type="button" onClick={() => setActiveTab(t.id)}
                  className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                  data-testid={`tab-${t.id}`}>
                  {t.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="place-form">

              {/* === BASIC INFO TAB === */}
              {activeTab === "basic" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Name *</label>
                      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Ganesh Temple" data-testid="form-place-name" />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Category *</label>
                      <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}>
                        <SelectTrigger data-testid="form-place-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                        <SelectContent>{PLACE_CATEGORIES.map((cat) => (<SelectItem key={cat} value={cat}>{categoryIcons[cat]} {cat}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Description</label>
                    <RichTextEditor value={form.description} onChange={(value) => setForm({ ...form, description: value })} placeholder="Brief description of this place..." minHeight="120px" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Location</label>
                      <Select value={form.location_id || "none"} onValueChange={(value) => setForm({ ...form, location_id: value === "none" ? "" : value, sub_location_id: "" })}>
                        <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {locations.map((loc) => (<SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Village/Area</label>
                      <Select value={form.sub_location_id || "none"} onValueChange={(value) => setForm({ ...form, sub_location_id: value === "none" ? "" : value })} disabled={!form.location_id}>
                        <SelectTrigger><SelectValue placeholder={form.location_id ? "Select village" : "Select location first"} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {subLocations.map((sl) => (<SelectItem key={sl.id} value={sl.id}>{sl.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Address</label>
                    <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Full address" />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Google Maps Link</label>
                    <Input value={form.map_link} onChange={(e) => setForm({ ...form, map_link: e.target.value })} placeholder="https://maps.google.com/..." />
                  </div>
                </div>
              )}

              {/* === MEDIA TAB === */}
              {activeTab === "media" && (
                <div className="space-y-4">
                  <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} token={token} label="Primary Image" />

                  {/* Image Carousel */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Image Carousel (additional images)</label>
                    {form.images.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {form.images.map((img, idx) => (
                          <div key={idx} className="relative aspect-video rounded-lg overflow-hidden border group">
                            <img src={img} alt={`Carousel ${idx + 1}`} className="w-full h-full object-cover" />
                            <button type="button" onClick={() => removeImage(idx)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <ImageUpload
                      value=""
                      onChange={(url) => { if (url) setForm({ ...form, images: [...form.images, url] }); }}
                      token={token}
                      label="Add Carousel Image"
                    />
                  </div>

                  {/* YouTube */}
                  <div>
                    <label className="text-sm font-medium mb-1 block">YouTube Videos</label>
                    {form.youtube_urls.length > 0 && (
                      <div className="mb-2 space-y-2">
                        {form.youtube_urls.map((url, index) => (
                          <div key={index} className="flex items-center gap-2 bg-red-50 rounded-lg p-2">
                            <Youtube className="w-4 h-4 text-red-500 flex-shrink-0" />
                            <span className="text-sm truncate flex-1">{url}</span>
                            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => setForm({ ...form, youtube_urls: form.youtube_urls.filter((_, i) => i !== index) })}><X className="w-4 h-4" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input value={newYoutubeUrl} onChange={(e) => setNewYoutubeUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." className="flex-1" />
                      <Button type="button" variant="outline" onClick={() => { if (newYoutubeUrl.trim()) { setForm({ ...form, youtube_urls: [...form.youtube_urls, newYoutubeUrl.trim()] }); setNewYoutubeUrl(""); } }} disabled={!newYoutubeUrl.trim()}><Plus className="w-4 h-4" /></Button>
                    </div>
                  </div>

                  {/* Instagram */}
                  <div>
                    <label className="text-sm font-medium mb-1 block">Instagram Reels</label>
                    {form.instagram_reel_urls.length > 0 && (
                      <div className="mb-2 space-y-2">
                        {form.instagram_reel_urls.map((url, index) => (
                          <div key={index} className="flex items-center gap-2 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-2">
                            <Instagram className="w-4 h-4 text-pink-500 flex-shrink-0" />
                            <span className="text-sm truncate flex-1">{url}</span>
                            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-pink-500" onClick={() => setForm({ ...form, instagram_reel_urls: form.instagram_reel_urls.filter((_, i) => i !== index) })}><X className="w-4 h-4" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input value={newInstagramUrl} onChange={(e) => setNewInstagramUrl(e.target.value)} placeholder="https://www.instagram.com/reel/ABC123..." className="flex-1" />
                      <Button type="button" variant="outline" onClick={() => { if (newInstagramUrl.trim()) { setForm({ ...form, instagram_reel_urls: [...form.instagram_reel_urls, newInstagramUrl.trim()] }); setNewInstagramUrl(""); } }} disabled={!newInstagramUrl.trim()}><Plus className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              )}

              {/* === TRAVEL INFO TAB === */}
              {activeTab === "travel" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block flex items-center gap-1"><Bus className="w-4 h-4" /> Nearest Bus Stand</label>
                      <Input value={form.nearest_bus_stand} onChange={(e) => setForm({ ...form, nearest_bus_stand: e.target.value })} placeholder="e.g., Alibaug Bus Stand (2 km)" data-testid="form-bus-stand" />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block flex items-center gap-1"><Train className="w-4 h-4" /> Nearest Railway Station</label>
                      <Input value={form.nearest_railway_station} onChange={(e) => setForm({ ...form, nearest_railway_station: e.target.value })} placeholder="e.g., Pen Railway Station (35 km)" data-testid="form-railway" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block flex items-center gap-1"><Car className="w-4 h-4" /> Distance from Mumbai</label>
                      <Input value={form.distance_from_mumbai} onChange={(e) => setForm({ ...form, distance_from_mumbai: e.target.value })} placeholder="e.g., 110 km (3 hours)" data-testid="form-dist-mumbai" />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block flex items-center gap-1"><Car className="w-4 h-4" /> Distance from Pune</label>
                      <Input value={form.distance_from_pune} onChange={(e) => setForm({ ...form, distance_from_pune: e.target.value })} placeholder="e.g., 145 km (3.5 hours)" data-testid="form-dist-pune" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block flex items-center gap-1"><Ticket className="w-4 h-4" /> Entry Fee</label>
                      <Input value={form.entry_fee} onChange={(e) => setForm({ ...form, entry_fee: e.target.value })} placeholder="e.g., Free / Rs. 50 per person" data-testid="form-entry-fee" />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block flex items-center gap-1"><Sun className="w-4 h-4" /> Best Season</label>
                      <Select value={form.best_season || "none"} onValueChange={(val) => setForm({ ...form, best_season: val === "none" ? "" : val })}>
                        <SelectTrigger data-testid="form-best-season"><SelectValue placeholder="Select best season" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not specified</SelectItem>
                          {BEST_SEASONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* === DETAILS TAB (Things To Do / Avoid) === */}
              {activeTab === "details" && (
                <div className="space-y-4">
                  {/* Things To Do */}
                  <div>
                    <label className="text-sm font-medium mb-2 block flex items-center gap-1"><ListChecks className="w-4 h-4" /> Things To Do</label>
                    {form.things_to_do.length > 0 && (
                      <div className="mb-2 space-y-1">
                        {form.things_to_do.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2">
                            <span className="text-sm flex-1">{item}</span>
                            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => setForm({ ...form, things_to_do: form.things_to_do.filter((_, i) => i !== idx) })}><X className="w-3 h-3" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input value={newTodo} onChange={(e) => setNewTodo(e.target.value)} placeholder="e.g., Visit the sunset point" className="flex-1"
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (newTodo.trim()) { setForm({ ...form, things_to_do: [...form.things_to_do, newTodo.trim()] }); setNewTodo(""); } } }} />
                      <Button type="button" variant="outline" onClick={() => { if (newTodo.trim()) { setForm({ ...form, things_to_do: [...form.things_to_do, newTodo.trim()] }); setNewTodo(""); } }} disabled={!newTodo.trim()}><Plus className="w-4 h-4" /></Button>
                    </div>
                  </div>

                  {/* Things To Avoid */}
                  <div>
                    <label className="text-sm font-medium mb-2 block flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Things To Avoid</label>
                    {form.things_to_avoid.length > 0 && (
                      <div className="mb-2 space-y-1">
                        {form.things_to_avoid.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-red-50 rounded-lg px-3 py-2">
                            <span className="text-sm flex-1">{item}</span>
                            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => setForm({ ...form, things_to_avoid: form.things_to_avoid.filter((_, i) => i !== idx) })}><X className="w-3 h-3" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input value={newAvoid} onChange={(e) => setNewAvoid(e.target.value)} placeholder="e.g., Don't litter the beach" className="flex-1"
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (newAvoid.trim()) { setForm({ ...form, things_to_avoid: [...form.things_to_avoid, newAvoid.trim()] }); setNewAvoid(""); } } }} />
                      <Button type="button" variant="outline" onClick={() => { if (newAvoid.trim()) { setForm({ ...form, things_to_avoid: [...form.things_to_avoid, newAvoid.trim()] }); setNewAvoid(""); } }} disabled={!newAvoid.trim()}><Plus className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              )}

              {/* === FAQS TAB === */}
              {activeTab === "faqs" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium flex items-center gap-1"><HelpCircle className="w-4 h-4" /> Frequently Asked Questions</label>
                    <Button type="button" variant="outline" size="sm" onClick={addFaq} data-testid="add-faq-btn"><Plus className="w-4 h-4 mr-1" /> Add FAQ</Button>
                  </div>
                  {form.faqs.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-6">No FAQs added yet. Click "Add FAQ" to start.</p>
                  )}
                  {form.faqs.map((faq, idx) => (
                    <div key={idx} className="border rounded-xl p-4 space-y-3 bg-muted/20" data-testid={`faq-item-${idx}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">FAQ #{idx + 1}</span>
                        <Button type="button" variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => removeFaq(idx)}><Trash2 className="w-3 h-3 mr-1" /> Remove</Button>
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1 block">Question</label>
                        <Input value={faq.question} onChange={(e) => updateFaq(idx, "question", e.target.value)} placeholder="e.g., What is the best time to visit?" data-testid={`faq-question-${idx}`} />
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1 block">Answer</label>
                        <textarea value={faq.answer} onChange={(e) => updateFaq(idx, "answer", e.target.value)} placeholder="Write the answer..." rows={3}
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          data-testid={`faq-answer-${idx}`} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* === SEO TAB === */}
              {activeTab === "seo" && (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Slug</label>
                    <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated-from-name" data-testid="form-slug" />
                    <p className="text-xs text-muted-foreground mt-1">Leave blank to auto-generate from name</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Meta Title</label>
                    <Input value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} placeholder="SEO title for search engines" data-testid="form-meta-title" />
                    <p className="text-xs text-muted-foreground mt-1">{form.meta_title?.length || 0}/60 characters</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Meta Description</label>
                    <textarea value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} placeholder="SEO description for search engines" rows={3}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid="form-meta-desc" />
                    <p className="text-xs text-muted-foreground mt-1">{form.meta_description?.length || 0}/160 characters</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Canonical URL</label>
                    <Input value={form.canonical_url} onChange={(e) => setForm({ ...form, canonical_url: e.target.value })} placeholder="https://kokanhomestays.in/place/... (leave empty for auto)" />
                    <p className="text-xs text-muted-foreground mt-1">Leave blank to use the default page URL</p>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="form-place-submit">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingPlace ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminPlaces;
